import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft, Star, MapPin, Phone, Shield, Calendar,
  CheckCircle, XCircle, MessageSquare, Trophy, Clock, ArrowRight
} from 'lucide-react';
import { getInitials, formatDate, formatTime } from '@/lib/utils';

export const RefereeProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referee, setReferee] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchReferee = async () => {
      try {
        const res = await api.get(`/referees/${userId}`);
        setReferee(res.data);
        setReviews(res.data.reviews || []);
      } catch (error) {
        console.error('Error fetching referee:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReferee();
  }, [userId]);

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-48" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (!referee) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Indietro
        </Button>
        <Card className="mt-6">
          <CardContent className="p-12 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold">Arbitro non trovato</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  const StarRating = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="referee-profile-page">
      <Button variant="ghost" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Indietro
      </Button>

      {/* Header Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <Avatar className="h-24 w-24">
              <AvatarImage src={referee.user?.picture} alt={referee.user?.name} />
              <AvatarFallback className="text-2xl">{getInitials(referee.user?.name)}</AvatarFallback>
            </Avatar>
            
            <div className="flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold">{referee.user?.name}</h1>
                <Badge variant={referee.is_available ? "default" : "secondary"}>
                  {referee.is_available ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Disponibile</>
                  ) : (
                    <><XCircle className="h-3 w-3 mr-1" /> Non disponibile</>
                  )}
                </Badge>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  <span className="font-bold text-lg">{referee.average_rating?.toFixed(1) || 'N/A'}</span>
                  <span className="text-muted-foreground">({referee.total_reviews || 0} recensioni)</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                {referee.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {referee.city} ({referee.coverage_radius_km || 20} km)
                  </span>
                )}
                {referee.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    {referee.phone}
                  </span>
                )}
              </div>

              {/* Sports */}
              <div className="flex flex-wrap gap-2">
                {referee.sports_details?.map(sport => (
                  <Badge key={sport.sport_id} variant="outline">
                    {sport.name}
                  </Badge>
                ))}
              </div>
            </div>

            {referee.is_available && (
              <Button className="shrink-0">
                <MessageSquare className="h-4 w-4 mr-2" />
                Contatta
              </Button>
            )}
          </div>

          {referee.bio && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-medium mb-2">Bio</h3>
              <p className="text-muted-foreground">{referee.bio}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Card */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-primary tabular-nums">{referee.matches_refereed || 0}</div>
            <div className="text-xs md:text-sm text-muted-foreground">Partite arbitrate</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-primary tabular-nums">{referee.leagues_refereed?.length || 0}</div>
            <div className="text-xs md:text-sm text-muted-foreground">Leghe seguite</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-primary tabular-nums">{referee.upcoming_matches?.length || 0}</div>
            <div className="text-xs md:text-sm text-muted-foreground">In agenda</div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming matches assigned to this referee */}
      {referee.upcoming_matches?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              Prossime partite assegnate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {referee.upcoming_matches.slice(0, 5).map((m) => (
                <Link
                  key={m.match_id}
                  to={`/matches/${m.match_id}`}
                  className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-muted/60 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{m.title}</p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground mt-0.5">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(m.date_time, { day: 'numeric', month: 'short' })} · {formatTime(m.date_time)}
                      </span>
                      {m.league?.name && <span>· {m.league.name}</span>}
                      {m.location_name && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {m.location_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leagues this referee has been called into */}
      {referee.leagues_refereed?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" />
              Leghe che lo chiamano
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {referee.leagues_refereed.map((lg) => (
                <Link
                  key={lg.league_id}
                  to={`/leagues/${lg.league_id}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted hover:bg-primary/15 hover:text-primary transition-colors text-xs font-medium"
                >
                  <Trophy className="h-3 w-3" />
                  <span>{lg.name}</span>
                  {lg.matches_count > 0 && (
                    <span className="text-[10px] text-muted-foreground tabular-nums">· {lg.matches_count}</span>
                  )}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Recensioni ({reviews.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reviews.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nessuna recensione ancora
            </p>
          ) : (
            <div className="space-y-4">
              {reviews.map((review, index) => (
                <div key={index} className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={review.reviewer_picture} />
                        <AvatarFallback>{getInitials(review.reviewer_name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{review.reviewer_name}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(review.created_at)}</p>
                      </div>
                    </div>
                    <StarRating rating={review.rating} />
                  </div>
                  {review.comment && (
                    <p className="text-sm text-muted-foreground">{review.comment}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RefereeProfilePage;
